import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, post, del } from "../../lib/request"
import {
  mapCompsetCompetitors,
  unwrapClientCompsetResponse,
} from "../../lib/utils/compsetMappers"

function resolveCompsetFetchArg(arg) {
  if (arg != null && typeof arg === "object") {
    return {
      clientId: arg.clientId,
      force: Boolean(arg.force),
    }
  }
  return { clientId: arg, force: false }
}

/** GET /api/v1/compsets/clients/{clientId} */
export const fetchClientCompset = createAsyncThunk(
  "clientCompset/fetch",
  async (arg, { rejectWithValue }) => {
    const { clientId } = resolveCompsetFetchArg(arg)
    if (clientId == null || clientId === "") {
      return rejectWithValue("Missing client id")
    }
    try {
      const response = await get(`compsets/clients/${clientId}`)
      const data = unwrapClientCompsetResponse(response)
      const competitors = mapCompsetCompetitors(data?.competitors)
      return {
        clientId: String(clientId),
        competitors,
        reportDefinitionId: data?.reportDefinitionId ?? null,
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch competitors")
    }
  },
  {
    condition: (arg, { getState }) => {
      const { clientId, force } = resolveCompsetFetchArg(arg)
      if (clientId == null || clientId === "") return false
      if (force) return true
      const { loading, clientId: storedId, error } = getState().clientCompset
      const same = String(storedId ?? "") === String(clientId)
      if (loading && same) return false
      if (same && !loading && !error) return false
      return true
    },
  },
)

/** POST /api/v1/compsets/clients/{clientId} */
export const addClientCompsetCompetitor = createAsyncThunk(
  "clientCompset/addCompetitor",
  async ({ clientId, entityId, order }, { rejectWithValue }) => {
    if (clientId == null || clientId === "") {
      return rejectWithValue("Missing client id")
    }
    const entityNum = Number(entityId)
    if (!Number.isFinite(entityNum) || entityNum <= 0) {
      return rejectWithValue("Missing entity id")
    }
    const orderNum = Number(order)
    try {
      const response = await post(`compsets/clients/${clientId}`, {
        entityId: entityNum,
        order: Number.isFinite(orderNum) && orderNum > 0 ? orderNum : 1,
      })
      return {
        clientId: String(clientId),
        entityId: entityNum,
        order: Number.isFinite(orderNum) && orderNum > 0 ? orderNum : 1,
        response,
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to add competitor to compset")
    }
  },
)

/** DELETE /api/v1/compsets/{id} */
export const deleteClientCompsetCompetitor = createAsyncThunk(
  "clientCompset/deleteCompetitor",
  async (compsetId, { rejectWithValue }) => {
    const idNum = Number(compsetId)
    if (!Number.isFinite(idNum) || idNum <= 0) {
      return rejectWithValue("Missing compset id")
    }
    try {
      await del(`compsets/${idNum}`)
      return { compsetId: String(idNum) }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to remove competitor")
    }
  },
)

const initialState = {
  clientId: null,
  competitors: [],
  reportDefinitionId: null,
  loading: false,
  adding: false,
  deleting: false,
  error: null,
  addError: null,
  deleteError: null,
}

const clientCompsetSlice = createSlice({
  name: "clientCompset",
  initialState,
  reducers: {
    clearClientCompset: () => initialState,
    addCompetitorLocal: (state, action) => {
      const competitor = action.payload
      if (!competitor) return
      state.competitors = [...state.competitors, competitor]
    },
    removeCompetitorLocal: (state, action) => {
      const id = String(action.payload ?? "")
      state.competitors = state.competitors.filter((c) => String(c.id) !== id)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientCompset.pending, (state, action) => {
        state.loading = true
        state.error = null
        const { clientId } = resolveCompsetFetchArg(action.meta?.arg)
        if (clientId != null && clientId !== "") {
          state.clientId = String(clientId)
        }
      })
      .addCase(fetchClientCompset.fulfilled, (state, action) => {
        state.loading = false
        state.clientId = action.payload.clientId
        state.competitors = action.payload.competitors ?? []
        state.reportDefinitionId = action.payload.reportDefinitionId ?? null
      })
      .addCase(fetchClientCompset.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || "Failed to fetch competitors"
        state.competitors = []
      })
      .addCase(addClientCompsetCompetitor.pending, (state) => {
        state.adding = true
        state.addError = null
      })
      .addCase(addClientCompsetCompetitor.fulfilled, (state) => {
        state.adding = false
        state.addError = null
      })
      .addCase(addClientCompsetCompetitor.rejected, (state, action) => {
        state.adding = false
        state.addError = action.payload || "Failed to add competitor to compset"
      })
      .addCase(deleteClientCompsetCompetitor.pending, (state) => {
        state.deleting = true
        state.deleteError = null
      })
      .addCase(deleteClientCompsetCompetitor.fulfilled, (state, action) => {
        state.deleting = false
        state.deleteError = null
        const compsetId = String(action.payload?.compsetId ?? "")
        if (!compsetId) return
        state.competitors = state.competitors.filter(
          (c) => String(c.id) !== compsetId && String(c.compsetId) !== compsetId,
        )
      })
      .addCase(deleteClientCompsetCompetitor.rejected, (state, action) => {
        state.deleting = false
        state.deleteError = action.payload || "Failed to remove competitor"
      })
  },
})

export const { clearClientCompset, addCompetitorLocal, removeCompetitorLocal } =
  clientCompsetSlice.actions
export default clientCompsetSlice.reducer
